import React, {type ComponentType, useEffect, useState} from 'react';

import {colors} from 'js-libs/sweetcommons/styles';
import {useSelector} from 'react-redux';
import styled from 'styled-components';

import {FullHTMLLink} from 'app/components/general/FullHTMLLink';
import {StyledSidebarH5} from 'app/general/bootstrap/utils';
import {
  InfiniteListRTK,
  type InfiniteListRTKRenderProps,
} from 'app/general/datafetchers/InfiniteListRTK';
import {SearchInputField} from 'app/general/widgets/SearchInputField';
import StateButton from 'app/general/widgets/StateButton';
import {setSidebarListState} from 'app/procedures/actions';
import {useModelChangeSubscription} from 'app/subscriptions';
import {SORT_BY_TYPES} from 'app/utils/constants';
import {gt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';

import DashboardSidebarCollapsableListItem from './DashboardSidebarCollapsableListItem';

import type {PermissionCodeType} from 'app/general/types';
import type {UseListQueryType} from 'app/store/sweet-api/api-query';

interface Props<
  IT extends {id: number; html_url?: string},
  FP extends {page: string}
> {
  ItemComponent?: ComponentType<any>;
  icon: string;
  title: string;
  reduxKey: string;
  showSearch: boolean;
  searchPlaceholder?: string;
  noResultsMessage: JSX.Element | string;
  canCreate?: boolean;
  createLink?: string;
  itemProps?: Record<string, unknown>;
  RelatedLink?: ComponentType<any>;
  itemNameProcessor: (item: any) => string | JSX.Element;
  watchModelChange: string;
  fetchParams?:
    | {
        permission: PermissionCodeType;
      }
    | Record<string, never>;
  useDataHook: UseListQueryType<FP, IT>;
  paramsConverter: (query: Record<string, any>) => FP;
  pageParam?: string;
}

export function DashboardSidebarCollapsableList<
  IT extends {id: number; html_url?: string},
  FP extends {page: string}
>({
  icon,
  title,
  showSearch,
  searchPlaceholder,
  ItemComponent = DashboardSidebarCollapsableListItem,
  itemNameProcessor,
  canCreate,
  createLink,
  RelatedLink,
  noResultsMessage,
  watchModelChange,
  useDataHook,
  paramsConverter,
  pageParam,
  itemProps,
  fetchParams = {},
  reduxKey,
}: Props<IT, FP>) {
  // Loading state is lifted out of the (open-only) list so the section header
  // can show a spinner while the first page loads.
  const [isLoading, setIsLoading] = useState(false);
  const actions = useAsyncActions({setSidebarListState});

  const {open, searchQuery} = useSelector((state) => {
    let sidebarState = state.proceduresPage.getIn([
      'dashboardSidebar',
      reduxKey,
    ]);
    sidebarState = sidebarState != null ? sidebarState.toJS() : {};
    return {
      open: false,
      searchQuery: '',
      ...sidebarState,
    };
  });

  const openStateIcon = !open
    ? 'fa fa-chevron-right'
    : isLoading
    ? 'fa fa-circle-notch fa-spin'
    : 'fa fa-chevron-down';

  const setReduxState = (state: Record<string, unknown>) => {
    actions.setSidebarListState(reduxKey, state);
  };

  const toggleOpen = () => setReduxState({open: !open});

  const updateSearchLoadItems = (searchQuery: string) =>
    setReduxState({searchQuery});

  return (
    <StyledContainerDiv className={open ? 'open' : ''}>
      <StyledSidebarH5 className="d-flex align-items-center">
        <a className="title text-truncate" onClick={toggleOpen}>
          <i className={`toggle far fa-fw ${openStateIcon} mr-2`} />
          <i className={`d-md-none d-lg-inline fa ${icon} mr-1`} /> {title}
        </a>
        {canCreate && createLink != null ? (
          <FullHTMLLink
            className="btn btn-link text-secondary btn-sm ml-auto"
            title={gt('Create')}
            html_url={createLink}
          >
            <i className="fa fa-plus-circle" />
          </FullHTMLLink>
        ) : null}
        {RelatedLink != null ? <RelatedLink /> : null}
      </StyledSidebarH5>
      {/* Only mount the list (and its fetch) while the section is open. */}
      {open ? (
        <div className="">
          {showSearch ? (
            <SearchInputField
              initialValue={searchQuery}
              onChange={updateSearchLoadItems}
              placeholder={searchPlaceholder != null ? searchPlaceholder : ''}
            />
          ) : null}
          <InfiniteListRTK
            fetchParams={{
              ...fetchParams,
              search: searchQuery,
              ordering: SORT_BY_TYPES.ALPHABETHICALLY,
            }} // It's OK to pass a new object here, because
            // we are relying on ``fetchParams`` stringification in useInfiniteListFetcherRTK
            useDataHook={useDataHook}
            paramsConverter={paramsConverter}
            pageParam={pageParam}
          >
            {(list) => (
              <DashboardSidebarList
                {...list}
                watchModelChange={watchModelChange}
                ItemComponent={ItemComponent}
                itemNameProcessor={itemNameProcessor}
                itemProps={itemProps}
                noResultsMessage={noResultsMessage}
                onLoadingChange={setIsLoading}
              />
            )}
          </InfiniteListRTK>
        </div>
      ) : null}
    </StyledContainerDiv>
  );
}

type DashboardSidebarListProps<IT extends {id: number; html_url?: string}> =
  InfiniteListRTKRenderProps<IT> & {
    watchModelChange: string;
    ItemComponent: ComponentType<any>;
    itemNameProcessor: (item: any) => string | JSX.Element;
    itemProps?: Record<string, unknown>;
    noResultsMessage: JSX.Element | string;
    onLoadingChange: (isLoading: boolean) => void;
  };

function DashboardSidebarList<IT extends {id: number; html_url?: string}>({
  results,
  loadNext,
  hasNext,
  isLoading,
  isFetching,
  error,
  refetch,
  watchModelChange,
  ItemComponent,
  itemNameProcessor,
  itemProps,
  noResultsMessage,
  onLoadingChange,
}: DashboardSidebarListProps<IT>) {
  useModelChangeSubscription(
    (message, {runWhenAnyModelMatch}) => {
      if (watchModelChange != null) {
        runWhenAnyModelMatch(message, watchModelChange, refetch);
      }
    },
    [watchModelChange]
  );

  // Report loading up for the header chevron; reset it when we unmount (close).
  useEffect(() => {
    onLoadingChange(isLoading);
  }, [isLoading, onLoadingChange]);
  useEffect(() => () => onLoadingChange(false), [onLoadingChange]);

  return (
    <ul className="list-unstyled pb-3 mb-0">
      {!isLoading && results.length === 0 ? (
        <li className="no-results">{noResultsMessage}</li>
      ) : null}

      {results.map((item) => (
        <ItemComponent
          key={item.html_url || item.id}
          nameProcessor={itemNameProcessor}
          item={item}
          {...itemProps}
        />
      ))}
      {!isLoading && error == null && (hasNext || isFetching) ? (
        <li>
          <StateButton
            isIdle={!isFetching}
            className="load-more mt-3 btn btn-sm btn-outline-primary"
            busyText={gt('Loading...')}
            onClick={loadNext}
          >
            {gt('Load more')}
          </StateButton>
        </li>
      ) : null}
    </ul>
  );
}

const StyledContainerDiv = styled.div.attrs<{className?: string}>({
  className: 'my-4',
})`
  ul {
    li:not(.tags) {
      margin: 0 !important;
      font-size: 90%;

      a {
        padding: 5px 0;
        display: block;
      }
    }

    li.no-results {
      color: ${colors.mediumGray};
    }
  }

  input[type='search'] {
    font-size: 90%;
  }

  .load-more {
    font-size: 80%;
  }
`;
